import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiClient } from '../services/api';
import { loadMapSettings } from '../services/googleMaps';
import { getDeviceId } from '../offline/device';
import { saveOfflineAuthorizationEnvelope, currentOfflineAuthorizationEnvelope, envelopeValid, clearOfflineAuthorizationForUser } from '../offline/authEnvelope';
import { probeConnectivity } from '../offline/connectivity';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  phone?: string;
  must_change_password?: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (identifier: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// React 18 StrictMode mounts effects twice in development. Cache the single
// page-lifetime auth probe so /auth/me is never duplicated by the dev-only
// double mount. A full page reload creates a fresh module/probe as expected.
let authProbePromise: Promise<any> | null = null;

const probeCurrentUser = () => {
  if (!authProbePromise) {
    authProbePromise = apiClient
      .get('/auth/me')
      .finally(() => {
        authProbePromise = null;
      });
  }

  return authProbePromise;
};

const hydrateAuthenticatedServices = () => {
  // Settings are authenticated in this deployment. Load them only after auth
  // so unauthenticated app startup does not emit /settings 401 noise.
  void loadMapSettings();
};

const refreshOfflineEnvelope = async () => {
  const appVersion = (import.meta as any).env?.VITE_APP_VERSION || '1.5.0';
  // Keep authentication routes free of custom CORS headers. The version is
  // application data, not transport metadata, so send it in the JSON body.
  const response = await apiClient.post('/auth/offline-envelope', { deviceId: getDeviceId(), appVersion });
  if (response.data?.envelope) await saveOfflineAuthorizationEnvelope(response.data.envelope);
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Login by email OR phone.
   * The backend /auth/login accepts either field; we detect which one was supplied
   * by checking for an @ sign — if present, send as email, otherwise as phone.
   */
  const login = async (identifier: string, password: string): Promise<User> => {
    try {
      const payload = identifier.includes('@')
        ? { email: identifier, password }
        : { phone: identifier, password };
      const response = await apiClient.post('/auth/login', payload);

      if (response.data && response.data.user) {
        const userData = response.data.user;
          setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        hydrateAuthenticatedServices();
        // Online authentication success must not be rolled back by an optional
        // offline-envelope renewal failure. Offline capability will remain
        // restricted until a later successful renewal.
        try { await refreshOfflineEnvelope(); } catch { /* non-fatal after successful online login */ }
        return userData;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error: any) {
      const status = error.response?.status;
      const retryHeader = error.response?.headers?.['retry-after'];
      const retryAfter = retryHeader ? Number.parseInt(String(retryHeader), 10) : undefined;
      const message = status === 429
        ? (Number.isFinite(retryAfter) ? `Too many sign-in attempts. Try again in ${retryAfter} seconds.` : 'Too many sign-in attempts. Please wait before trying again.')
        : (error.response?.data?.error || error.message || 'Login failed');
      const loginError = new Error(message) as Error & { status?: number; retryAfter?: number };
      loginError.status = status;
      if (Number.isFinite(retryAfter)) loginError.retryAfter = retryAfter;
      throw loginError;
    }
  };

  const logout = async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      const userId = user?.id;
      setUser(null);
      localStorage.removeItem('user');
      if (userId) { try { await clearOfflineAuthorizationForUser(userId); } catch { /* unsynced evidence is preserved; only the authorization envelope is removed */ } }
    }
  };

  const refreshUser = useCallback(async () => {
    try {
      const response = await apiClient.get('/users/me');
      if (response.data?.profile) {
        const profile = response.data.profile;
        const updated = {
          id: profile.id,
          email: profile.email,
          name: profile.name,
          role: profile.role,
          phone: profile.phone,
          must_change_password: profile.must_change_password,
        };
        setUser(updated);
        localStorage.setItem('user', JSON.stringify(updated));
        try { await refreshOfflineEnvelope(); } catch { /* profile refresh remains usable if envelope renewal fails */ }
      }
    } catch (e) {
      // ignore — non-fatal
    }
  }, []);

  const checkAuth = useCallback(async () => {
    let storedUser: User | null = null;
    try {
      const raw = localStorage.getItem('user');
      if (raw) storedUser = JSON.parse(raw);
    } catch {
      localStorage.removeItem('user');
    }

    try {
      const connectivity = await probeConnectivity();
      if (connectivity.state === 'OFFLINE' || connectivity.state === 'NETWORK_PRESENT_API_UNREACHABLE' || connectivity.state === 'BACKEND_UNAVAILABLE') {
        if (storedUser) {
          const envelope = await currentOfflineAuthorizationEnvelope(storedUser.id);
          if (envelopeValid(envelope)) {
            setUser(storedUser);
            setLoading(false);
            return;
          }
        }
        setUser(null);
        setLoading(false);
        return;
      }

      const response = await probeCurrentUser();
      if (response.status === 200 && response.data?.user) {
        const userData = response.data.user;
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        hydrateAuthenticatedServices();
        try { await refreshOfflineEnvelope(); } catch { /* online auth remains valid; offline capability will show restricted */ }
      } else {
        setUser(null);
        localStorage.removeItem('user');
      }
    } catch {
      // If the online auth probe itself failed because connectivity degraded after
      // the reachability check, permit only a still-valid bounded envelope.
      if (storedUser) {
        try {
          const envelope = await currentOfflineAuthorizationEnvelope(storedUser.id);
          if (envelopeValid(envelope)) { setUser(storedUser); setLoading(false); return; }
        } catch {}
      }
      setUser(null);
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handleAuthExpired = () => {
      setUser(null);
      localStorage.removeItem('user');
    };

    window.addEventListener(
      'coconnect:auth-expired',
      handleAuthExpired
    );

    return () => {
      window.removeEventListener(
        'coconnect:auth-expired',
        handleAuthExpired
      );
    };
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      isAuthenticated: !!user,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
